// WHAT: React hook for fetching and managing stored Yazio nutrition data from database
// WHY:  Provides state management and API integration for retrieving historical nutrition data stored locally
// DOMAIN: Health → Yazio → Data Storage
// DEPENDENCIES: Tauri core API, React hooks, database storage service
// RISKS:
//   - NOTE: Auto-loads on mount - may cause errors if credentials are not configured
//   - NOTE: Error handling throws error but also sets state - may cause double error handling
//   - WARN: Date parameter is optional but validation is minimal - may cause unexpected queries
//   - NOTE: Refresh logic assumes existing data structure - may fail if data format changes

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

export interface YazioStoredDataEntry {
  carbs: number;
  created_at: string;
  date: string;
  fat: number;
  id: string;
  protein: number;
  total_calories: number;
  updated_at: string;
}

export interface YazioStoredDataResponse {
  count: number;
  data: YazioStoredDataEntry[];
}

interface YazioStoredDataState {
  data: undefined | YazioStoredDataResponse;
  error: string | undefined;
  isLoading: boolean;
  lastUpdated: Date | undefined;
}

/**
 * React hook for fetching and managing stored Yazio nutrition data from the database.
 *
 * @returns Object containing stored data state, loading status, error state, and data management functions.
 *
 */
export function useYazioStoredData() {
  const [state, setState] = useState<YazioStoredDataState>({
    data: undefined,
    error: undefined,
    isLoading: false,
    lastUpdated: undefined,
  });

  const fetchStoredData = useCallback(async (date?: string) => {
    try {
      setState((previous) => ({ ...previous, error: undefined, isLoading: true }));

      const response = await core.invoke<YazioStoredDataResponse>("get_yazio_stored_data", {
        date,
      });

      setState((previous) => ({
        ...previous,
        data: response,
        isLoading: false,
        lastUpdated: new Date(),
      }));

      return response;
    } catch (error) {
      // NOTE: Failed to fetch stored Yazio data
      setState((previous) => ({
        ...previous,
        error: "Failed to load stored nutrition data",
        isLoading: false,
      }));
      throw error;
    }
  }, []);

  const loadAllData = useCallback(() => {
    return fetchStoredData();
  }, [fetchStoredData]);

  const loadDateData = (date: string) => {
    return fetchStoredData(date);
  };

  const clearError = () => {
    setState((previous) => ({ ...previous, error: undefined }));
  };

  const refreshData = () => {
    return state.data ? fetchStoredData() : loadAllData();
  };

  // Auto-load data on mount
  useEffect(() => {
    void loadAllData();
  }, [loadAllData]);

  return {
    ...state,
    clearError,
    fetchStoredData,
    loadAllData,
    loadDateData,
    refreshData,
  };
}
